<template>
  <div
    class="filter-stripe w-100"
    :class="{ 'event-only': eventOnly }"
    :style="additionalFilterStyle"
  >
    <AddFilterButton
      ref="eventFilterButton"
      class="lisaui:flex lisaui:shrink-0 lisaui:self-stretch"
      :filter-config="eventFilterConfig"
      :filters="eventFilters.eventFilters"
      :event-filters="eventFilters.eventFilters"
      :action-filters="actionFilters.actionFilters"
      :filter-type="eventFilterType"
      title="Ereignisse filtern"
      accent="event"
      :number-of="eventCount"
      :show-archive="showArchive"
      :types="eventTypes"
      :site-id="siteId"
      button-l10n-key="components.eventActionBoard.filter.EventActionFilter.filterEvents"
      @apply="applyEventFilters"
      @paste.stop
    />

    <FilterInputView
      class="filter-input-view"
      :event-filters="eventFilters.eventFilters"
      :action-filters="actionFilters.actionFilters"
      :event-types="eventTypes"
      :action-types="actionTypes"
      :board-id="boardId"
      @filter-selected="filterSelected"
      @filter-removed="filterRemoved"
      @all-filters-removed="allFiltersRemoved"
      @refresh-filter="refreshFilter"
    />

    <AddFilterButton
      v-if="!eventOnly"
      ref="actionFilterButton"
      class="lisaui:flex lisaui:shrink-0 lisaui:self-stretch"
      :filter-config="actionFilterConfig"
      :filters="actionFilters.actionFilters"
      :event-filters="eventFilters.eventFilters"
      :action-filters="actionFilters.actionFilters"
      :filter-type="actionFilterType"
      title="Aktionen filtern"
      accent="action"
      :number-of="actionCount"
      :show-archive="showArchive"
      :types="actionTypes"
      :site-id="siteId"
      button-l10n-key="components.eventActionBoard.filter.EventActionFilter.filterActions"
      @apply="applyActionFilters"
      @paste.stop
    />
  </div>
</template>

<script setup lang="ts">
import { ref, type PropType, computed, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import AddFilterButton from "./filter/AddFilterButton.vue";
import FilterInputView from "./filter/FilterInputView.vue";
import {
  type ActionFilter,
  type EventFilter,
  Filter,
  FilterEntity,
  type ActionFilters,
  type EventFilters,
  type FilterConfig,
  type FilterEvent,
  type FilterEventActionTypes,
  type BoardFilterOptions,
} from "./filter/FilterTypes";
import { EABFilterType } from "@/graphql";
import {
  BoardOriginFilter,
  CreatorFilter,
  DateFilter,
  DLZFilter,
  EventActionRelationFilter,
  FlagsFilter,
  IDFilter,
  OverdueFilter,
  PriorityFilter,
  RecentlyChangedFilter,
  StatusFilter,
} from "./filter/filterImplementations";
import CustomAttributeFilter from "./filter/filterImplementations/CustomAttributeFilter";
import DescriptionDraftFilter from "./filter/filterImplementations/DescriptionDraftFilter";
import { provideBoardFilterOptions } from "./filter/useBoardFilterOptions";
import { useSQDCGlobalSettings } from "@/modules/sqdc/helper/useSQDCGlobalSettings";
const props = defineProps({
  showArchive: {
    type: Boolean,
    required: false,
    default: false,
  },
  eventTypes: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
  actionTypes: {
    type: Array as PropType<FilterEventActionTypes[]>,
    required: false,
    default: () => [],
  },
  boardId: {
    type: Number,
    required: false,
    default: undefined,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
  eventCount: {
    type: Number,
    required: false,
    default: 0,
  },
  actionCount: {
    type: Number,
    required: false,
    default: 0,
  },
  eventOnly: {
    type: Boolean,
    required: false,
    default: false,
  },
  filterOptions: {
    type: Object as PropType<BoardFilterOptions>,
    required: false,
    default: undefined,
  },
  showQuickSearch: {
    type: Boolean,
    required: false,
    default: true,
  },
});
const eventFilters = defineModel("eventFilters", {
  type: Object as PropType<EventFilters>,
  required: true,
});
const actionFilters = defineModel("actionFilters", {
  type: Object as PropType<ActionFilters>,
  required: true,
});
const quickSearch = defineModel("quickSearch", {
  type: String,
  required: true,
});
const quickSearchInput = ref(quickSearch.value);
const applyQuickSearch = useDebounceFn((value: string) => {
  quickSearch.value = value.trim();
}, 400);
watch(quickSearchInput, (value) => applyQuickSearch(value));
watch(quickSearch, (value) => {
  if (value !== quickSearchInput.value.trim()) {
    quickSearchInput.value = value;
  }
});
const eventFilterType = computed(() => eventFilters.value.type ?? EABFilterType.AND);
const actionFilterType = computed(() => actionFilters.value.type ?? EABFilterType.AND);
const { showEisenhowerPriority } = useSQDCGlobalSettings();
const { t } = useTranslation();

provideBoardFilterOptions(
  computed(() => props.boardId),
  computed(() => props.showArchive),
  computed(() => props.filterOptions),
);
const eventFilterConfig = computed<FilterConfig[]>(() => {
  const filters: FilterConfig[] = [
    {
      id: 0,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.IDFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: IDFilter as any,
      category: "base",
    },
    {
      id: 5,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.FlagsFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: FlagsFilter,
    },
    {
      id: 3,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.CreatorFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: CreatorFilter,
    },
    {
      id: 1,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.DateFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: DateFilter,
    },
    {
      id: 4,
      text: t(
        "modules.sqdc.components.eventActionBoard.filter.labels.DescriptionFilter",
      ).toString(),
      mode: FilterEntity.EVENT,
      filter: DescriptionDraftFilter,
    },
    {
      id: 11,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.CustomAttributesTitle"),
      mode: FilterEntity.EVENT,
      filter: CustomAttributeFilter,
    },
    {
      id: 2,
      text: t(
        "modules.sqdc.components.eventActionBoard.filter.labels.BoardOriginFilter",
      ).toString(),
      mode: FilterEntity.EVENT,
      filter: BoardOriginFilter,
      collapsed: true,
    },
    {
      id: 7,
      text: t(
        "modules.sqdc.components.eventActionBoard.filter.labels.RecentlyChangedFilter",
      ).toString(),
      mode: FilterEntity.EVENT,
      filter: RecentlyChangedFilter,
      collapsed: true,
    },
    {
      id: 8,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.DLZFilter").toString(),
      mode: FilterEntity.EVENT,
      filter: DLZFilter,
      collapsed: true,
    },
    {
      id: 9,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.EventActionRelationFilter"),
      mode: FilterEntity.EVENT,
      filter: EventActionRelationFilter,
      collapsed: true,
    },
  ];
  if (showEisenhowerPriority.value === true) {
    filters.splice(1, 0, {
      id: 10,
      text: t("modules.sqdc.components.eventActionBoard.filter.labels.priorityFilter"),
      mode: FilterEntity.EVENT,
      filter: PriorityFilter,
    });
  }
  return filters;
});
const actionFilterConfig = computed<FilterConfig[]>(() => [
  {
    id: 0,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.IDFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: IDFilter as any,
    category: "base",
  } as FilterConfig,
  {
    id: 3,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.ResponsibleFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: CreatorFilter,
  },
  {
    id: 1,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.DateFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: DateFilter,
  },
  {
    id: 4,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.DescriptionFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: DescriptionDraftFilter,
  },
  {
    id: 7,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.CustomAttributesTitle"),
    mode: FilterEntity.ACTION,
    filter: CustomAttributeFilter,
  },
  {
    id: 2,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.StatusFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: StatusFilter,
    collapsed: true,
  },
  {
    id: 5,
    text: t(
      "modules.sqdc.components.eventActionBoard.filter.labels.RecentlyChangedFilter",
    ).toString(),
    mode: FilterEntity.ACTION,
    filter: RecentlyChangedFilter,
    collapsed: true,
  },
  {
    id: 6,
    text: t("modules.sqdc.components.eventActionBoard.filter.labels.OverdueFilter").toString(),
    mode: FilterEntity.ACTION,
    filter: OverdueFilter as any,
    collapsed: true,
  },
]);
const eventFilterButton = ref<null | InstanceType<typeof AddFilterButton>>(null);
const actionFilterButton = ref<null | InstanceType<typeof AddFilterButton>>(null);
const additionalFilterStyle = computed<Record<string, string> | undefined>(() => {
  if (props.showArchive) {
    return {
      "background-color": "var(--lisa-info)",
    };
  }
  return undefined;
});
function applyEventFilters(filters: Filter[], type: EABFilterType) {
  eventFilters.value = {
    updatedAt: DateTime.now(),
    eventFilters: filters.filter((filter): filter is EventFilter => "getEventFilter" in filter),
    type,
  };
}
function applyActionFilters(filters: Filter[], type: EABFilterType) {
  actionFilters.value = {
    updatedAt: DateTime.now(),
    actionFilters: filters.filter((filter): filter is ActionFilter => "getActionFilter" in filter),
    type,
  };
}
function filterRemoved({ index, type }: FilterEvent) {
  if (type === FilterEntity.EVENT) {
    const currentFilters = eventFilters.value.eventFilters.filter((_, i) => i !== index);
    eventFilters.value = {
      updatedAt: DateTime.now(),
      eventFilters: currentFilters,
      type: eventFilterType.value,
    };
  } else {
    const currentFilters = actionFilters.value.actionFilters.filter((_, i) => i !== index);
    actionFilters.value = {
      updatedAt: DateTime.now(),
      actionFilters: currentFilters,
      type: actionFilterType.value,
    };
  }
}
function filterSelected({ index, type }: FilterEvent) {
  if (type === FilterEntity.EVENT) {
    eventFilterButton.value?.loadFilter(eventFilters.value.eventFilters[index]);
  } else if (type === FilterEntity.ACTION) {
    actionFilterButton.value?.loadFilter(actionFilters.value.actionFilters[index]);
  }
}
function allFiltersRemoved() {
  eventFilters.value = {
    updatedAt: DateTime.now(),
    eventFilters: [],
    type: eventFilterType.value,
  };
  actionFilters.value = {
    updatedAt: DateTime.now(),
    actionFilters: [],
    type: actionFilterType.value,
  };
}
function refreshFilter() {
  eventFilters.value = {
    ...eventFilters.value,
    updatedAt: DateTime.now(),
  };
  actionFilters.value = {
    ...actionFilters.value,
    updatedAt: DateTime.now(),
  };
}
</script>

<style lang="scss" scoped>
.filter-stripe {
  position: sticky;
  z-index: 9;
  top: 0;
  display: flex;
  min-height: 3rem;
  align-items: center;
  gap: 0.4rem;
  margin: 0.75rem 0 0.3rem;
  padding: 0.4rem;
  border-radius: var(--p-border-radius-md);
  background: var(--p-primary-color);
}

.filter-input-view {
  min-width: 0;
  flex: 1;
  align-self: stretch;
  border: 0 !important;
  border-radius: 0 !important;
}

@media (max-width: 48rem) {
  .filter-stripe {
    gap: 0.25rem;
    padding: 0.25rem;
  }

  .filter-input-view {
    overflow: hidden;
  }
}
</style>
